import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CtaComponent from '../../../../components/reusable-ui/cta';
import {
  CTA_SIZE,
  CTA_TYPE,
  CURRENT_CURRENCY_SYMBOL,
} from '../../../../data/constants';
import { fetchCommonData } from '../../../../redux/slices';
import { ENDPOINT } from '../../../../services/apiUrls';
import { Modal } from '@mui/material';
import { Box } from '@mui/system';
import TextFieldInput from '../../../../components/reusable-ui/textfield-box';
import CustomDropdown from '../../../../components/reusable-ui/custom-dropdown';
import { allowAllNumbers } from '../../../../utils';
import { convertArrayToObject } from '../../../../helpers/catalog-helper';
import { toastSuccess } from '../../../../utils/react-toaster';
import { METHODS } from '../../../../data/api-method-constants';
import SideBar from './sidebar';
import Body from './body';
import Header, { renderStatusInfo } from './header';

export interface TransactionData {
  name?: string;
  status: string;
  deviceList: any[];
  deviceLabels: string[];
  openInfo?: any;
  startTime: string;
  endTime: string;
  prev: any;
  next: any;
  variances: any[];
  openingVariances: any[];
  transactions: any;
  collections: any;
  settlementNumber: any;
  nextSettlementInfo?: any;
  previousSettlementInfo?: any;
  userInfo: any;
  eventLog: any[];
  balanceStatus: string;
  myself?: any;
  others?: any;
  mid: any;
}

interface formValueData {
  remarks?: string;
  transactionType?: string;
  amount: number;
  branchId?: string;
  settlementInfo?: any;
  mode: string;
}

export const RESOLVE_VARIANCE = 'RESOLVE_VARIANCE';
export const ADJUST_VARIANCE = 'ADJUST_VARIANCE';
export const ADD_CASH = 'ADD_CASH';
export const DEPOSIT = 'DEPOSIT';

const ViewSettlement = (props: any) => {
  let initialState: TransactionData = {
    name: '',
    status: '',
    deviceList: [],
    deviceLabels: [],
    startTime: '',
    endTime: '',
    userInfo: {},
    mid: '',
    settlementNumber: '',
    prev: { name: '', id: null },
    next: { name: '', id: null },
    variances: [],
    openingVariances: [],
    transactions: [],
    eventLog: [],
    balanceStatus: '',
    collections: [],
    myself: {},
    others: {}
  };
  const initialSettlementId = props.settlementId ;
  const [data, setData] = useState(initialState);
  const [settlementId, setSettlementId] = useState(props.settlementId);
  const [modalState, setModalState] = useState({
    header: '',
    subText: '',
    show: false,
    type: '',
  });
  const paymentModesMap = useSelector((state: any) => {
    return convertArrayToObject(
      state.businessReducer?.businessData?.paymentdefs?.filter((value:any) => value.settlement),
      'mode'
    );
  });
  const [formValues, setFormValues] = useState<formValueData>({
    amount: 0,
    remarks: '',
    mode: '',
    branchId: '',
    settlementInfo: { settlementId: settlementId },
    transactionType: '',
  });

  const dispatch = useDispatch();
  
  useEffect(() => {
    getSettlementData();
  }, [settlementId]);
  
  function getSettlementData() {
    dispatch(
      fetchCommonData({
        method: METHODS.GET,
        url:
          ENDPOINT.ACCOUNTING.getSettlement +
          '/' +
          settlementId +
          '/fetch',
        callback: (res: any) => {
          setData(res);
          setFormValues({
            ...formValues,
            amount: 0,
            remarks: '',
            transactionType: '',
            mode: '',
            branchId: res.branchId,
          });
        },
      })
    );
  }
  const CTA_CONFIG = {
    addCash: {
      ctaSize: CTA_SIZE.medium,
      ctaText: '+ ADD CASH',
      ctaType: CTA_TYPE.primary_cta,
      onCtaClick: () => {
        setFormValues({ ...formValues, transactionType: 'Float' });
        setModalState({
          ...modalState,
          header: 'Add Cash',
          subText: 'Add balance to your cash drawer',
          show: true,
          type: ADD_CASH,
        });
      },
      customClass: 'h-10 w-[20%] p-0 sm:text-[13px] sm:w-[81px] text-[#397EF5]',
    },
    resolveVariance: {
      ctaSize: CTA_SIZE.medium,
      ctaText: 'RESOLVE VARIANCE',
      ctaType: CTA_TYPE.primary_cta,
      onCtaClick: () =>
        setModalState({
          ...modalState,
          header: 'Resolve Variance',
          type: RESOLVE_VARIANCE,
          subText: 'Please mention reason of variances below',
          show: true,
        }),
      customClass: 'h-10 w-[20%] sm:text-[13px] sm:w-[45%] p-0 text-[#397EF5]',
    },
    cancelTransaction: {
      ctaSize: CTA_SIZE.medium,
      ctaText: modalState.type !== RESOLVE_VARIANCE ? 'Discard' : 'Cancel',
      ctaType: CTA_TYPE.secondary_cta,
      onCtaClick: closeModal,
      customClass: 'ml-auto',
    },
    saveTransaction: {
      ctaSize: CTA_SIZE.medium,
      ctaText: modalState.type !== RESOLVE_VARIANCE ? 'Save' : 'Resolve',
      ctaType: CTA_TYPE.active_cta,
      onCtaClick: saveTransaction,
      isDisabled:
        modalState.type === RESOLVE_VARIANCE
          ? formValues.remarks === ''
          : formValues.amount === 0 || formValues.mode === '',
      customClass: 'ml-12',
    },
  };

  const TEXT_CONFIG = {
    remarks: {
      textBoxLabel: 'Remarks',
      value: formValues.remarks,
      parentClass: 'mt-24',
      customClass: 'p-16 text-14',
      placeholder:
        modalState.type === RESOLVE_VARIANCE ||
        modalState.type === ADJUST_VARIANCE
          ? 'Please mention how variance is resolved'
          : 'Enter Remarks',
      maxLength: 100,
      rows: modalState.type !== RESOLVE_VARIANCE ? 3 : 7,
      handleChange: (value: any) =>
        setFormValues({ ...formValues, remarks: value }),
    },
    amount: {
      value: formValues.amount,
      textBoxLabel: 'Amount',
      parentClass: 'w-[47.5%]',
      customClass: 'p-24 text-15',
      buttonConfig: {
        left: { text: CURRENT_CURRENCY_SYMBOL, customClass: 'p-24' },
      },
      regexCheck: allowAllNumbers,
      handleChange: (value: any) =>
        setFormValues({ ...formValues, amount: value ? value : 0 }),
    },
  };

  function handleTransactionResponse(res: any) {
    if (res.mid) {
      toastSuccess(getSuccessMessage());
      closeModal();
    }
  }

  function getSuccessMessage() {
    let message = '';
    switch (modalState.type) {
      case RESOLVE_VARIANCE:
        message = 'Variance Resolved';
        break;
      case DEPOSIT:
        message =
          'Branch account has been deposited with ' +
          formValues.amount +
          ' ' +
          formValues.mode;
        break;
      case ADJUST_VARIANCE:
        message =
          'Cash drawer is adjusted with ' +
          -1 * formValues.amount +
          ' ' +
          formValues.mode;
        break;
      case ADD_CASH:
        message =
          'Cash drawer is credited with ' +
          formValues.amount +
          ' ' +
          formValues.mode;
        break;
    }
    return message;
  }

  function saveTransaction() {
    let payload = {} as formValueData;
    if (modalState.type === RESOLVE_VARIANCE) {
      payload['remarks'] = formValues.remarks;
    } else {
      payload = {
        ...formValues,
        amount: parseFloat(formValues.amount.toString()),
      };
    }
    dispatch(
      fetchCommonData({
        method: METHODS.POST,
        body: payload,
        url:
          modalState.type === RESOLVE_VARIANCE
            ? ENDPOINT.ACCOUNTING.resolveVariance(settlementId)
            : ENDPOINT.ACCOUNTING.saveTransaction,
        callback: (res: any) => handleTransactionResponse(res),
      })
    );
  }

  function renderNextPrevCashier() {
    let isPrev = data.previousSettlementInfo,
      isNext = data.nextSettlementInfo;
    return (
      (isPrev || isNext) && (
        <div className="hidden text-black sm:flex w-full fixed z-10 bottom-0 h-66 bg-white pt-11 pb-16 px-[19px] border-t-[1px] ">
          {initialSettlementId === data.mid && isPrev && (
            <div
              onClick={() => setSettlementId(isPrev.settlementId)}
              className={`flex items-center ${
                isNext ? 'flex-col w-[50%] ' : ''
              } mx-auto`}
            >
              <div className="opacity-40">Previous cashier:</div>
              <div className={`flex items-center ${isNext ? 'mt-4' : 'ml-7'} `}>
                {data.previousSettlementInfo?.userInfo?.userName?.firstName}{' '}
                <div className="rista-sprite ic-arrow-left-blue rotate-180"></div>
              </div>
            </div>
          )}
          {initialSettlementId === data.mid && isNext && (
            <div
              onClick={() => setSettlementId(isPrev.settlementId)}
              className={`flex items-center ${
                isPrev ? 'flex-col w-[50%] ' : ''
              } mx-auto`}
            >
              <div className="opacity-40">Next cashier:</div>
              <div className={`flex items-center ${isPrev ? 'mt-4' : 'ml-7'} `}>
                {data.nextSettlementInfo?.userInfo?.userName?.firstName}{' '}
                <div className="rista-sprite ic-arrow-left-blue rotate-180"></div>
              </div>
            </div>
          )}
          {initialSettlementId !== data.mid && (
            <div
              onClick={() => setSettlementId(initialSettlementId)}
              className={`flex items-center mx-auto`}
            >
              <div className="rista-sprite mr-8 ic-arrow-left-blue"></div>
              <div>Back to Current Cashier </div>
            </div>
          )}
        </div>
      )
    );
  }

  function closeModal() {
    getSettlementData();
    setModalState({
      ...modalState,
      header: '',
      subText: '',
      type: '',
      show: false,
    });
  }

  function renderTextField(textConfig: any) {
    return <TextFieldInput {...textConfig} />;
  }

  function resolveVarianceModal() {
    return (
      <Modal open={modalState.show} onClose={closeModal}>
        <Box className="modal-center w-[50%] bg-white">
          <div className="flex justify-between pl-30 pr-20 pt-18 modal-header">
            <h2 className="text-22 font-semibold">{modalState.header}</h2>
            <div
              className="rista-sprite ic-cross-grey"
              onClick={closeModal}
            ></div>
          </div>
          <div className="modal-body-content px-30">
            <span className="text-black/50">{modalState.subText}</span>
            {modalState.type !== RESOLVE_VARIANCE && (
              <div className="flex mt-[23px] justify-between">
                {renderTextField(TEXT_CONFIG.amount)}
                <CustomDropdown
                  placeholder={'Select payment mode'}
                  isDisabled={
                    modalState.type === ADJUST_VARIANCE ||
                    modalState.type === DEPOSIT
                  }
                  textBoxLabel="Payment Mode"
                  parentClass="w-[47.5%]"
                  onSubmit={(value: any) =>
                    setFormValues({ ...formValues, mode: value[0] })
                  }
                  preSelectedValues={formValues.mode}
                  optionsMap={paymentModesMap}
                  uniqueKey={'mode'}
                  getListItemText={(val: any) => val?.mode || ''}
                />
              </div>
            )}
            {renderTextField(TEXT_CONFIG.remarks)}
            <div className="modal-footer flex px-30 pb-18 pt-0">
              {renderCTA(CTA_CONFIG.cancelTransaction)}
              {renderCTA(CTA_CONFIG.saveTransaction)}
            </div>
          </div>
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <div className="flex flex-col bg-white mt-24 mx-20 sm:mx-0 sm:mt-0 rounded-[6px] h-[90vh] sm:h-auto overflow-hidden sm:overflow-visible sm:pb-16">
        <Header data={data} formValues={formValues} setFormValues={setFormValues} modalState={modalState} setModalState={setModalState} toggleViewSettlement={props.toggleViewSettlement} />
        <div className="flex mt-24 mb-[100px] sm:mb-0 sm:pb-0 sm:flex-col h-[100%] sm:mt-0">
          <span className="hidden sm:flex w-full ">{renderStatusInfo(data)}</span>
          <SideBar data={data} toggleViewSettlement={setSettlementId} initialSettlementId={initialSettlementId} />
          <Body data={data} formValues={formValues} setFormValues={setFormValues} modalState={modalState} setModalState={setModalState} />
        </div>
      </div>
      {resolveVarianceModal()}
      {renderNextPrevCashier()}
    </>
  );
};

export function renderCTA(ctaConfig: any) {
  return <CtaComponent {...ctaConfig}></CtaComponent>;
}

export default ViewSettlement;
